import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import PhotoDropzone from "@/components/upload/PhotoDropzone";
import UploadProgress from "@/components/upload/UploadProgress";
import {
  uploadFilesToR2,
  type PresignedFile,
  type UploadItem,
} from "@/lib/r2Upload";
import { readStoredSession } from "@/lib/interviewSession";

type Tribute = {
  id: string;
  slug: string;
  full_name: string;
  status: string;
};

type PresignResponse = {
  tribute_id: string;
  prefix: string;
  expires_in: number;
  uploads: Array<{
    url: string;
    key: string;
    content_type: string;
    original_name: string;
  }>;
};

// supabase-js wraps a non-2xx edge response into a FunctionsHttpError whose
// .context is the raw Response. The consent gate returns 403 { error:
// "consent_incomplete" }; detect exactly that so it stays distinguishable from a
// generic upload/enqueue failure. Mirrors InterviewLanding's error.context read.
const isConsentIncomplete = async (error: unknown): Promise<boolean> => {
  const context = (error as { context?: Response } | null)?.context;
  if (!context || typeof context.status !== "number" || context.status !== 403) {
    return false;
  }
  try {
    const body = (await context.clone().json()) as { error?: string };
    return body?.error === "consent_incomplete";
  } catch {
    return false;
  }
};

const CONSENT_MESSAGE = "Please complete consent before uploading photos/video.";

const PhotoUpload = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [tribute, setTribute] = useState<Tribute | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Interview resume token from sessionStorage — the ONLY authorization for
  // photogrammetry upload. sessionChecked flips true once we've looked, so the
  // "no session" block doesn't flash before the read.
  const [resumeToken, setResumeToken] = useState<string | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  const [files, setFiles] = useState<File[]>([]);
  const [items, setItems] = useState<ReadonlyArray<UploadItem>>([]);
  const [submitState, setSubmitState] = useState<
    "idle" | "presigning" | "uploading" | "completing" | "done" | "error"
  >("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!slug) return;
    supabase
      .from("tributes")
      .select("id, slug, full_name, status")
      .eq("slug", slug)
      .single()
      .then(({ data, error }) => {
        if (error || !data) {
          setNotFound(true);
        } else {
          setTribute(data as Tribute);
        }
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setResumeToken(readStoredSession(slug)?.resume_token ?? null);
    setSessionChecked(true);
  }, [slug]);

  // Warn user before navigating away mid-upload.
  useEffect(() => {
    if (submitState !== "uploading" && submitState !== "presigning" && submitState !== "completing") {
      return;
    }
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [submitState]);

  const startUpload = async () => {
    if (!slug || !resumeToken || files.length === 0) return;
    setSubmitError(null);
    setSubmitState("presigning");

    try {
      const presignResp = await supabase.functions.invoke<PresignResponse>(
        "r2-presign-photos",
        {
          headers: { Authorization: `Bearer ${resumeToken}` },
          body: {
            slug,
            files: files.map((f) => ({
              name: f.name,
              size: f.size,
              type: f.type,
            })),
          },
        }
      );

      if (presignResp.error || !presignResp.data) {
        if (await isConsentIncomplete(presignResp.error)) {
          setSubmitError(CONSENT_MESSAGE);
          setSubmitState("error");
          return;
        }
        throw new Error(
          presignResp.error?.message ?? "Failed to get upload URLs"
        );
      }

      const { uploads } = presignResp.data;
      if (uploads.length !== files.length) {
        throw new Error("Server returned wrong number of upload URLs");
      }

      const presignedFiles: PresignedFile[] = files.map((file, i) => ({
        file,
        url: uploads[i].url,
        key: uploads[i].key,
        contentType: uploads[i].content_type,
        originalName: uploads[i].original_name,
      }));

      setSubmitState("uploading");
      const controller = new AbortController();
      abortRef.current = controller;

      const result = await uploadFilesToR2(presignedFiles, {
        concurrency: 6,
        signal: controller.signal,
        onUpdate: (next) => setItems([...next]),
      });

      if (result.failedKeys.length > 0) {
        setSubmitState("error");
        setSubmitError(
          `${result.failedKeys.length} photo(s) failed to upload. You can retry.`
        );
        return;
      }

      setSubmitState("completing");
      const completeResp = await supabase.functions.invoke<{
        job_id: string;
        tribute_id: string;
      }>("r2-photos-complete", {
        headers: { Authorization: `Bearer ${resumeToken}` },
        body: { slug, uploaded_keys: result.uploadedKeys },
      });

      if (completeResp.error || !completeResp.data) {
        if (await isConsentIncomplete(completeResp.error)) {
          setSubmitError(CONSENT_MESSAGE);
          setSubmitState("error");
          return;
        }
        throw new Error(
          completeResp.error?.message ?? "Failed to finalize upload"
        );
      }

      setSubmitState("done");
      // Photos are in and the tribute has flipped to "processing" — send the
      // subject back into the interview to record their answers, not to the
      // passive processing page. The resume session is still live, so the
      // ?stage=post_photo_handoff landing resumes and validates before honoring it.
      navigate(`/interview/${slug}?stage=post_photo_handoff`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      console.error("[PhotoUpload]", msg);
      setSubmitError(msg);
      setSubmitState("error");
    }
  };

  const retryFailed = async () => {
    // Items still hold prior state; the uploader retries items whose status is
    // "failed" by re-running. Simplest approach: rebuild the presigned list
    // for failed items only, using the URLs we already have (still valid for
    // ~1h). We don't store them across re-renders, so user retries by
    // re-submitting; presign will be re-issued. For v1 this is acceptable.
    setItems([]);
    await startUpload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (notFound || !tribute) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold mb-3">Tribute not found</h1>
          <p className="text-muted-foreground mb-6">
            This upload link is invalid or expired.
          </p>
          <Link to="/" className="text-primary hover:underline text-sm">
            ← Back to LegacyLens AI
          </Link>
        </div>
      </div>
    );
  }

  if (tribute.status !== "awaiting_photos") {
    const message =
      tribute.status === "processing"
        ? "Photos for this tribute are already being processed."
        : tribute.status === "ready"
        ? "This tribute is already complete."
        : "This tribute isn't accepting photos right now.";
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="font-display text-3xl font-bold mb-3">{tribute.full_name}</h1>
          <p className="text-muted-foreground mb-6">{message}</p>
          {tribute.status === "processing" && (
            <Link to={`/processing/${slug}`} className="text-primary hover:underline">
              Check processing status →
            </Link>
          )}
          {tribute.status === "ready" && (
            <Link to={`/tribute/${slug}`} className="text-primary hover:underline">
              View tribute →
            </Link>
          )}
        </div>
      </div>
    );
  }

  const canSubmit =
    !!resumeToken &&
    sessionChecked &&
    files.length > 0 &&
    submitState === "idle";

  const isUploading =
    submitState === "presigning" ||
    submitState === "uploading" ||
    submitState === "completing";

  return (
    <main className="min-h-screen bg-background">
      <div className="px-4 py-3 flex justify-center">
        <Link to="/" className="opacity-50 hover:opacity-80 transition-opacity">
          <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-7" />
        </Link>
      </div>

      <div className="container mx-auto max-w-2xl px-6 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">
          Upload photos for {tribute.full_name}
        </h1>
        <p className="text-muted-foreground mb-8">
          We'll use these photos to build the 3D tribute. Need help capturing
          them?{" "}
          <Link to="/capture" className="text-primary hover:underline">
            Read the capture guide
          </Link>
          .
        </p>

        {/* No interview session -> blocked. No anonymous fallback: photogrammetry
            must not be reachable by slug alone; consent runs in the interview. */}
        {sessionChecked && !resumeToken && (
          <div className="mb-8 rounded-lg border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground">
              To upload photos, please open your interview link and complete
              consent first. This page can't be used on its own.
            </p>
          </div>
        )}

        {resumeToken && submitState === "idle" && (
          <PhotoDropzone onFilesSelected={setFiles} />
        )}

        {(isUploading || submitState === "error" || submitState === "done") && (
          <div className="space-y-4">
            <UploadProgress items={items} />
            {submitState === "presigning" && (
              <p className="text-sm text-muted-foreground">Preparing upload…</p>
            )}
            {submitState === "completing" && (
              <p className="text-sm text-muted-foreground">Finalizing…</p>
            )}
          </div>
        )}

        {submitError && (
          <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {submitError}
          </div>
        )}

        <div className="mt-8 flex gap-3">
          {canSubmit && (
            <Button onClick={startUpload}>Upload {files.length} photos</Button>
          )}
          {submitState === "error" && (
            <Button onClick={retryFailed} variant="outline">
              Retry
            </Button>
          )}
          {isUploading && (
            <Button
              variant="outline"
              onClick={() => abortRef.current?.abort()}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </main>
  );
};

export default PhotoUpload;
